import {
  createContext,
  createElement,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  type ReactNode,
} from "react";
import { useMutation } from "convex/react";
import type { FunctionArgs, FunctionReference, FunctionReturnType } from "convex/server";
import { api } from "@constructionautomation/backend";
import type { Id } from "@constructionautomation/backend/convex/_generated/dataModel";

type ActionLogPilot = "SpecPilot" | "RevPilot" | "TakeoffPilot" | "CollabPilot" | "DocPilot";
type ActionLogTargetKind =
  | "specPilotSubmittal"
  | "revPilotComparison"
  | "takeoffPilotJob"
  | "projectDocument"
  | "projectDocumentRevision";

interface ActionLogProviderProps {
  projectId?: Id<"projects"> | null;
  pilot: ActionLogPilot;
  targetKind: ActionLogTargetKind;
  targetId?: string | null;
  children: ReactNode;
}

interface ActionLogContextValue {
  recordAction: () => Promise<void>;
}

const ActionLogContext = createContext<ActionLogContextValue | null>(null);

export function ActionLogProvider({
  projectId,
  pilot,
  targetKind,
  targetId,
  children,
}: ActionLogProviderProps) {
  const recordAction = useMutation(api.actionLog.recordAction);
  const hasRecordedRef = useRef(false);
  const activeSessionKeyRef = useRef("");
  const sessionKey = useMemo(
    () => `${pilot}:${projectId ?? ""}:${targetKind}:${targetId ?? ""}`,
    [pilot, projectId, targetId, targetKind]
  );

  useEffect(() => {
    activeSessionKeyRef.current = sessionKey;
    hasRecordedRef.current = false;
  }, [sessionKey]);

  const recordSessionAction = useCallback(async () => {
    if (hasRecordedRef.current || !projectId || !targetId) {
      return;
    }

    hasRecordedRef.current = true;
    try {
      await recordAction({ projectId, pilot, targetKind, targetId });
    } catch (error) {
      if (activeSessionKeyRef.current === sessionKey) {
        hasRecordedRef.current = false;
      }
      console.error(error);
    }
  }, [pilot, projectId, recordAction, sessionKey, targetId, targetKind]);

  const value = useMemo(
    () => ({ recordAction: recordSessionAction }),
    [recordSessionAction]
  );

  return createElement(ActionLogContext.Provider, { value }, children);
}

export function useRecordActionLog() {
  const context = useContext(ActionLogContext);
  if (!context) {
    return async () => {};
  }

  return context.recordAction;
}

export function useLoggedMutation<Mutation extends FunctionReference<"mutation", "public">>(
  mutation: Mutation
) {
  const mutate = useMutation(mutation);
  const recordAction = useRecordActionLog();

  return useCallback(
    async (args: FunctionArgs<Mutation>): Promise<FunctionReturnType<Mutation>> => {
      const result = await mutate(args);
      await recordAction();
      return result;
    },
    [mutate, recordAction]
  );
}
